import * as actionTypes from './actionTypes';

export const INITIAL_STATE = {
	advisories: [],
	pagination: {},
	advisoriesLoading: true,
	advisoriesError: null,
	saving: false,
	savingError: null,
	showFormModal: false,
	editedAdvisory: null,
	advisorySeed: null,
	advisoryToRevoke: null
};

/*
 * `advisoriesLoading` starts true so the table shows its preloader before the first response
 * lands, rather than flashing "no advisories have been added yet" at someone whose catalog is
 * still on the wire.
 *
 * One `saving` flag rather than one per gesture: creating, editing and revoking are mutually
 * exclusive, and a single flag is what lets the form modal, the revoke dialog and the table's row
 * actions all disable off the same truth - so a second write cannot race the first.
 */
const firmwareAdvisoryReducer = (state = INITIAL_STATE, action) => {
	switch (action.type) {
		case actionTypes.START_FETCHING_FIRMWARE_ADVISORIES:
			return { ...state, advisoriesLoading: true, advisoriesError: null };
		case actionTypes.GET_FIRMWARE_ADVISORIES_SUCCESS:
			return {
				...state,
				advisories: action.payload.advisories,
				pagination: action.payload.pagination,
				advisoriesLoading: false,
				advisoriesError: null
			};
		case actionTypes.GET_FIRMWARE_ADVISORIES_ERROR:
			return { ...state, advisoriesLoading: false, advisoriesError: action.payload };

		case actionTypes.START_SAVING_FIRMWARE_ADVISORY:
			return { ...state, saving: true, savingError: null };
		case actionTypes.SAVE_FIRMWARE_ADVISORY_SUCCESS:
			return { ...state, saving: false, savingError: null };
		case actionTypes.SAVE_FIRMWARE_ADVISORY_ERROR:
			return { ...state, saving: false, savingError: action.payload };
		/*
		 * Deliberately not an error: the write was abandoned, not refused, so there is no reason to
		 * show anybody who comes back a message about it.
		 *
		 * Every flag the write raised comes down together, the open modal included - clearing
		 * `saving` alone would still leave a returning user staring at a dialog for a write that is
		 * no longer running. All three modal fields are cleared rather than only the one that was
		 * open: create, edit and revoke are mutually exclusive, so the others are already at rest,
		 * and the abort carries no record of which gesture it interrupted. `editedAdvisory` is the
		 * load-bearing one - left behind, the next "Add advisory" opens pre-filled with it.
		 */
		case actionTypes.SAVE_FIRMWARE_ADVISORY_ABORTED:
			return {
				...state,
				saving: false,
				savingError: null,
				showFormModal: false,
				editedAdvisory: null,
				advisorySeed: null,
				advisoryToRevoke: null
			};

		case actionTypes.SHOW_FIRMWARE_ADVISORY_FORM_MODAL:
			return {
				...state,
				showFormModal: true,
				editedAdvisory: action.payload ?? null,
				advisorySeed: null,
				savingError: null
			};
		case actionTypes.SHOW_SEEDED_FIRMWARE_ADVISORY_FORM_MODAL:
			return {
				...state,
				showFormModal: true,
				editedAdvisory: null,
				advisorySeed: action.payload ?? null,
				savingError: null
			};
		/*
		 * Clearing `editedAdvisory` and `advisorySeed` on hide is load-bearing: left behind, the next
		 * "Add advisory" opens pre-filled with the advisory last edited or the approval last seeded.
		 */
		case actionTypes.HIDE_FIRMWARE_ADVISORY_FORM_MODAL:
			return { ...state, showFormModal: false, editedAdvisory: null, advisorySeed: null };

		case actionTypes.SHOW_FIRMWARE_ADVISORY_REVOKE_MODAL:
			return { ...state, advisoryToRevoke: action.payload ?? null, savingError: null };
		case actionTypes.HIDE_FIRMWARE_ADVISORY_REVOKE_MODAL:
			return { ...state, advisoryToRevoke: null };

		default:
			return state;
	}
};

export default firmwareAdvisoryReducer;
